/* Builder A · WF-08 seam types + hooks.
   Ruling 30/08: builder hooks live in the builder’s own folder, same
   get<T> transport and query-key style as lib/api.ts, so lifting them
   into the shared seam later is a move, not a rewrite.

   Batch 9 (06/09): a row is a `DestinationRef` of `@/lib/campaign` — the SAME
   object « Demandes › Liens d’avis » writes and the campaign stepper’s
   step 3 writes. This page is the technical view of it: the checks, the
   platform confirmations, what a pause would touch. The platform list is
   `PLATFORMS` (six); the url is READ FROM THE REF, never derived from the
   platform. */
import { useQuery } from '@tanstack/react-query'
import type { PlatformId } from '@/lib/campaign'

/* ---------- domain types (schema v3 vocabulary) ---------- */
export type DestTone = 'ok' | 'warn' | 'crit' | 'paused'

export interface Destination {
  id: string
  /** the platform of the ref — decides the lettermark and the detail screen */
  platform: PlatformId
  /** what the merchant reads: the platform’s label, or a custom link’s own name */
  name: string
  meta: string
  tone: DestTone
  label: string
  /** read from the ref (`DestinationRef.url`) — never rebuilt from the platform */
  url?: string
  /** a custom link’s glyph, from CUSTOM_ICONS */
  icon?: string
  /** the establishment this link belongs to, when the workspace has several */
  place?: string
  /** the ref's own establishment id — what `removeDestination` matches on */
  locationId?: string
  /** false for a row this page invented from the world’s history: it has no ref
   *  behind it yet, so « Retirer » would have nothing to remove */
  linked?: boolean
}
export interface DestinationsData {
  location: string
  items: Destination[]
  pendingAcks: number
}

/** DS-04 — link, reporting and response never imply one another. */
export interface Capability {
  id: string; name: string; meta: string; tone: DestTone; label: string
}
/** DS-05 — a listing mismatch pauses trust, not the journey. */
export interface ListingDrift {
  location: string
  stored: { name: string; address: string }
  live: { name: string; address: string }
}
export interface GoogleData {
  location: string
  capabilities: Capability[]
  drift: ListingDrift | null
}

/** DS-10 — merchant side of the §20 re-acknowledgement triggers. */
export interface AckItem {
  id: string; title: string; meta: string; action: string; primary: boolean
}
export interface AcksData { pending: AckItem[] }

/** DS-06 — what a public-request pause would actually touch. */
export interface PauseImpact {
  scope: string; fallback: string
  campaigns: number; stages: number; scheduledMessages: number
}

/** DS-09 — auth (sign in with Facebook) and Destination (link a Page) are
 *  two different things; one never authorises the other (§18). */
export interface FbPage { id: string; name: string; meta: string; eligible: boolean }

/** DS-07 — provider-wide disable, reserved for genuine necessity (§20). */
export interface EmergencyDisable {
  destination: string; reason: string; since: string
  fallback: string; reactivation: string
}

/* ---------- transport (mirrors lib/api.ts) ---------- */
async function get<T>(path: string): Promise<T> {
  const res = await fetch(`/v1${path}`)
  if (!res.ok) throw new Error(`${res.status} on ${path}`)
  return res.json() as Promise<T>
}

/* ---------- hooks — the only import WF-08 screens use ---------- */
export const useDestinations = () =>
  useQuery({ queryKey: ['destinations'], queryFn: () => get<DestinationsData>('/destinations') })
export const useGoogle = () =>
  useQuery({ queryKey: ['destinations', 'google'], queryFn: () => get<GoogleData>('/destinations/google') })
export const useAcks = () =>
  useQuery({ queryKey: ['destinations', 'acks'], queryFn: () => get<AcksData>('/destinations/acknowledgements') })
export const usePauseImpact = () =>
  useQuery({ queryKey: ['destinations', 'pause'], queryFn: () => get<PauseImpact>('/destinations/pause-impact') })
export const useFacebookPages = () =>
  useQuery({ queryKey: ['destinations', 'facebook'], queryFn: () => get<FbPage[]>('/destinations/facebook-pages') })
export const useEmergencyDisable = () =>
  useQuery({ queryKey: ['destinations', 'emergency'], queryFn: () => get<{ current: EmergencyDisable | null }>('/destinations/emergency') })
